import { useEffect, useRef, useState } from "react";
import type {
  DoorSignStatus,
  PremiseConfig,
  PremiseScreen,
  RealtimeEvent,
} from "@shared/types/foyer";
import { isDoorSignStatus, isDoorSignOrientation } from "@shared/types/foyer";
import { useConfig } from "./hooks/useConfig";
import SchemeRouter, { isSchemeKey } from "./schemes/SchemeRouter";
import {
  DEMO_OFFER_FEED,
  DEMO_OFFER_PROMO_SLOT,
} from "./schemes/_shared/offers/demoOffers";
import DoorSignSurface from "./door-sign/DoorSign";
import DoorSignPreviewSurface from "./door-sign/DoorSignPreview";
import SignageSurface from "./signage/SignageSurface";
import SignageEngineSurface from "./signage/SignageEngineSurface";
import CmsPreview from "./CmsPreview";
import BootScreen from "./components/BootScreen";
import PairingScreen from "./components/PairingScreen";
import PlatformProbe from "./components/PlatformProbe";
import PlayerProbe from "./diagnostics/PlayerProbeScreen";
import { noteBoot } from "./lib/bootTrace";
import { hostParams } from "./lib/hostQuery";
import { restartAppFresh } from "./lib/hostReload";
import MessageOverlay from "./components/MessageOverlay";
import OfflineBadge from "./components/OfflineBadge";
import { commitGoodShell } from "./lib/offlineShell";
import { connectRealtime } from "./lib/realtime";
import {
  ackCommand,
  commandToEvent,
  fetchPendingCommands,
} from "./lib/commands";
import { runLogCapture, runScreenshot } from "./lib/diagnostics";
import { runCheckoutWipe as runTvCheckoutWipe } from "./lib/checkoutWipe";
import { markExecuted, wasExecuted } from "./lib/commandDedupe";
import { captureRunning, takeInterruptedStage } from "./lib/captureTrail";
import { currentPlaybackTally } from "./lib/playbackTelemetry";
import {
  currentSignagePlayback,
  resetSignagePlayback,
} from "./lib/signagePlayback";
import { installLogBuffer } from "./lib/logBuffer";
import { startLogShipper } from "./lib/logShipper";
import { foregroundReport } from "./lib/foregroundWatch";
import { signageCacheReport } from "./signage/localAssetCache";
import { eventBus } from "./lib/eventBus";
import {
  clearBinding,
  getBinding,
  getSalvagedToken,
  hasRawBinding,
  getScreenId,
  setBinding,
  setScreenId,
  type Binding,
} from "./lib/binding";
import {
  bridge,
  deviceModel,
  devicePlatform,
  deviceCapabilities,
} from "./lib/foyerBridge";
import { demoCatalogue } from "./schemes/_shared/vod/demoCatalogue";
import { demoLineup } from "./schemes/_shared/guide/demoLineup";
import "./styles.css";

// Start buffering console output as soon as the bundle loads so a remote
// `log_capture` command has history to upload (Phase 3 diagnostics).
installLogBuffer();

// The Samsung platform inventory is logged from main.tsx, NOT here.
//
// It used to be called on this line, and the first capture off a real panel
// came back saying `registerKey=not attempted` - which was a bug in the
// reporting, not a failure on the TV. A module's imports are evaluated before
// the importing module's body, so this file's top level runs while main.tsx is
// still being loaded, i.e. BEFORE `installTizenKeyBridge()` has had a chance to
// claim anything. The inventory was reading the claim report before the claim.
// See main.tsx, where the order is explicit and commented.

// State this box's screen-sharing answer ONCE at boot, into that same buffer.
// Logging it only when a guest presses Screen Share means a log capture taken
// at any other moment - or after a reload cleared the buffer - says nothing,
// and "can this TV mirror a phone, and with what?" is precisely the question
// support asks when nobody is standing in the room. Written as a warning
// because the shell forwards only warnings and errors to logcat.
try {
  console.warn(
    "[screen-share] boot:",
    JSON.stringify({
      platform: bridge.platform,
      casting: bridge.getCastingInfo(),
      target: bridge.getScreenShareTarget(),
    })
  );
} catch {
  // Diagnostics must never keep the kiosk from booting.
}

/**
 * How many CONSECUTIVE 401 heartbeats it takes to conclude the CMS really did
 * remove this screen and unpair ourselves. At a 30 s ping interval this is
 * ~1 minute of sustained rejection - long enough to outlive a boot-time
 * captive portal, proxy or cold-start 401, short enough that a genuine unpair
 * still lands promptly. Never lower this to 1: on a device without a hardware
 * serial the unpair is irreversible without someone walking to the TV.
 */
export const UNAUTHORIZED_STRIKES = 3;

/**
 * How often to poll for commands the realtime channel did not deliver.
 *
 * Deliberately slower than the 30s heartbeat: this is a backstop, not a bus.
 * Realtime remains the fast path; this only guarantees that a device which
 * cannot hold that connection is still reachable at all.
 */
export const COMMAND_DRAIN_MS = 60_000;

/**
 * A sample Custom screen for `?scheme=<key>&on=custom&screen=custom`.
 *
 * The Custom screen is empty until a premise designs one, so without this there
 * is nothing to review: the design preview would show a black page and the one
 * surface whose whole point is how it LOOKS could only be judged after
 * configuring a real premise. Preview-only - it is passed from this branch
 * alone, never to a bound TV.
 */
const CUSTOM_PREVIEW = {
  items: [
    {
      id: "spa",
      label: "Spa",
      imageUrl:
        "https://images.unsplash.com/photo-1540555700478-4be289fbecef?w=1920&q=70",
      vignette: { size: 45, color: "#0b0d10" },
      text: {
        body: "Book an hour\nin the spa",
        placement: "middle-left" as const,
        fontSize: 96,
        color: "#ffffff",
      },
      qr: { url: "https://nenda.com", placement: "bottom-right" as const },
    },
    {
      id: "dining",
      label: "Dining",
      imageUrl:
        "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=1920&q=70",
      vignette: { size: 70, color: "#1a140e" },
      text: {
        body: "Dinner is served\nfrom six",
        placement: "top-right" as const,
        fontSize: 72,
        color: "#f1e6c8",
      },
    },
    {
      id: "walks",
      label: "Walks",
      imageUrl:
        "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=1920&q=70",
      text: {
        body: "Trails from the door",
        placement: "bottom-left" as const,
        fontSize: 64,
        color: "#ffffff",
      },
    },
  ],
};

/** The call windows `?scheme=care&call=…` can paint for a design review. */
const CALL_PREVIEWS = ["ring", "announce", "connecting", "failed"] as const;
type CallPreview = (typeof CALL_PREVIEWS)[number];

/**
 * Top-level routing. Decides between modes based on URL + local binding state:
 *   - scheme design preview   → <SchemeRouter> (no premise)
 *   - bound or preview premise → <Launcher premiseId=…>
 *   - unbound                 → <PairingScreen>
 * Pure routing - no hooks here so the chosen child can use hooks freely.
 */
export default function App() {
  const params = hostParams();

  // ?reset=1 clears the local pairing so the TV returns to the pairing
  // screen. Intentionally LOCAL ONLY - server-side unpair is a CMS
  // operation (requires login). A leaked URL with ?reset=1 only resets
  // the visitor's own browser state.
  if (params.get("reset") === "1") {
    noteBoot("reset_flag");
    clearBinding();
    restartAppFresh();
    return null;
  }

  // First-light diagnostics - `?probe=1` (TIZEN-2). Answered BEFORE every
  // other mode, including a bound TV's launcher, because the panels this
  // exists for are the ones where the launcher may not render at all. It
  // needs no premise, no binding and no network, and it discloses only facts
  // about the device (never the pairing token).
  if (params.get("probe") === "1") {
    return <PlatformProbe />;
  }

  // The on-device player probe - `?probe=player` (PLAYER-2 / PLAYER-4). Same
  // reasoning as `?probe=1` above, one layer down: it drives a real decoder and
  // prints the two tables `docs/player-hardware-tests.md` currently asks a
  // person at a panel to assemble by hand. Answered before every other mode
  // because the panels it exists for are the ones where playback - and possibly
  // the launcher - may not work at all; it needs no premise, no binding and no
  // pairing, and it discloses only facts about the device (licence URLs are
  // redacted, tokens never printed).
  if (params.get("probe") === "player") {
    return <PlayerProbe />;
  }

  // Live in-CMS preview - the editor embeds this in an iframe and pushes the
  // draft config via postMessage. `?preview=1` makes the server serve the TV
  // bundle; `cms=1` switches it into the postMessage-driven preview.
  if (params.get("cms") === "1") {
    return <CmsPreview />;
  }

  // Design preview of a scheme in isolation - `?scheme=atelier`
  // (optionally `&hotel=…&city=…&room=…`). Renders the bespoke scheme
  // full-screen against a synthetic config, with no backend, premise, or
  // pairing required. Used to review the schemes against their mocks.
  //
  // `&call=ring|announce|connecting|failed` additionally paints the incoming
  // call window over it (care only). The call surfaces are otherwise reviewable
  // for a few seconds, during a real call, on a real box - which is how they
  // drifted off-theme in the first place (VC3-9).
  const previewScheme = params.get("scheme") ?? undefined;
  if (isSchemeKey(previewScheme)) {
    const stubConfig = {
      premiseName: params.get("hotel") || "The Grand Nenda Hotel",
      address: { city: params.get("city") || "Stockholm" },
    } as PremiseConfig;
    const callRaw = params.get("call");
    const previewCall = CALL_PREVIEWS.includes(callRaw as CallPreview)
      ? (callRaw as CallPreview)
      : undefined;
    // `&on=vilmer,menu,wifi` forces those toggle knobs on. Every Information
    // screen is opt-in and off by default, so without this a design preview of
    // one shows an empty menu and the surface can only be reviewed by first
    // configuring a real premise.
    const previewKnobs = Object.fromEntries(
      (params.get("on") ?? "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
        .map((id) => [
          // Accept both the knob id and the screen id it controls, because a
          // reviewer knows the screen ("vilmer"), not the knob ("showVilmer").
          id.startsWith("show")
            ? id
            : `show${id[0]?.toUpperCase()}${id.slice(1)}`,
          true,
        ])
    );
    return (
      <div className="foyer-app-shell">
        <div className="foyer-app" data-testid="app">
          <SchemeRouter
            schemeKey={previewScheme}
            config={stubConfig}
            roomId={params.get("room") ?? undefined}
            previewCall={previewCall}
            previewKnobs={previewKnobs}
            previewContent={{
              ...(previewKnobs.showCustom ? { custom: CUSTOM_PREVIEW } : {}),
              // The home-screen offer banner (`POS-4`) needs BOTH switches, and
              // an authored fill on top: `?scheme=atelier&on=offers,promotions`.
              ...(previewKnobs.showOffers && previewKnobs.showPromotions
                ? { slots: DEMO_OFFER_PROMO_SLOT }
                : {}),
            }}
            // Same reasoning as CUSTOM_PREVIEW: the Offers screen is empty until
            // a hotel authors a catalogue, and a design surface that can only be
            // reviewed after seeding a database is one that gets designed blind.
            previewOffers={
              previewKnobs.showOffers ? DEMO_OFFER_FEED : undefined
            }
            // The same reasoning again, and here it is not a convenience but
            // the only option: Foyer production holds ZERO `packages`
            // rows, so every rail on a real bound television resolves to
            // nothing. Without these two there is no configuration anywhere -
            // local, staging or live - in which the `vod` and `guide` screens
            // can be looked at by a human.
            //   ?scheme=atelier&on=vod&screen=vod
            //   ?scheme=atelier&on=guide&screen=guide
            previewVod={previewKnobs.showVod ? demoCatalogue() : undefined}
            previewGuide={previewKnobs.showGuide ? demoLineup() : undefined}
          />
        </div>
      </div>
    );
  }

  // Door-sign design preview - `?doorsign=1` renders the corridor sign
  // full-screen against a synthetic projection, no backend/pairing required.
  // Simulate state via query params, e.g.
  //   ?doorsign=1&room=204&name=Astrid+L.&status=resting&occupied=1&hotel=Solgården&locale=sv
  if (params.get("doorsign") === "1") {
    const occupied = params.get("occupied") !== "0";
    const name = params.get("name") ?? "";
    const statusRaw = params.get("status") ?? "none";
    const status: DoorSignStatus = isDoorSignStatus(statusRaw)
      ? statusRaw
      : "none";
    const room = params.get("room") || "204";
    const orientationRaw = params.get("orientation");
    const orientation = isDoorSignOrientation(orientationRaw)
      ? orientationRaw
      : "landscape";
    const stub = {
      premiseName: params.get("hotel") || "Solgården",
      defaultLocale: params.get("locale") || "sv",
      rooms: [{ id: room }],
    } as PremiseConfig;
    return (
      <DoorSignSurface
        config={stub}
        roomId={room}
        orientation={orientation}
        preview
        previewProjection={{
          roomId: room,
          occupied,
          status,
          identityMode: name ? "full-name" : "none",
          displayName: occupied ? name : "",
        }}
      />
    );
  }

  // Door-sign preview of a REAL screen - `?doorsignScreen=<screenId>&premise=<id>`.
  // Distinct from the `?doorsign=1` design preview above (synthetic values from
  // the query string): this one renders the room's actual projection. Same
  // reason as signage below for being its own branch - the door-sign branch in
  // the Launcher keys off THIS device's screen id and is skipped in preview, so
  // the `preview=1` launcher could only ever show the in-room TV. Checked before
  // that launcher route; the CMS URL carries `preview=1` for the edge router.
  const previewDoorSign = params.get("doorsignScreen") ?? undefined;
  const doorSignPremise = params.get("premise") ?? undefined;
  if (previewDoorSign && doorSignPremise) {
    return (
      <DoorSignPreviewSurface
        premiseId={doorSignPremise}
        screenId={previewDoorSign}
      />
    );
  }

  // Signage preview of a REAL screen - `?signage=<screenId>&premise=<slug>`
  // (optionally `&at=<ISO>` to scrub: "what does this wall show on Sunday
  // morning?").
  //
  // Separate from the `preview=1` launcher below because a signage screen is
  // not a room: the role lives on the screen record, a preview tab has no
  // screen id of its own (`getScreenId()` mints one for THIS browser), and it
  // has no screen token, so the panel's own endpoint would refuse it. This
  // branch asks the operator-scoped timeline route instead, authenticated by
  // the CMS cookie that the `.nenda.com` scope already shares with this host.
  //
  // Diagnostics are ON here and off on the wall: black is the correct output
  // for a screen with nothing scheduled, and the person who needs to know
  // WHICH kind of nothing it is is the one at the laptop.
  const previewSignage = params.get("signage") ?? undefined;
  const signagePremise = params.get("premise") ?? undefined;
  if (previewSignage && signagePremise) {
    return (
      <SignageSurface
        screenId={previewSignage}
        source={{ kind: "operator", premiseId: signagePremise }}
        at={params.get("at") ?? undefined}
        diagnostics
      />
    );
  }

  // CMS preview of a specific premise - `?premise=X&room=Y&preview=1`.
  // The flag itself confers no access; the server still requires the
  // CMS cookie (which travels automatically on this same origin) or a
  // screen-token. Heartbeat + SSE are skipped because they need a real
  // screen-token, which preview tabs don't have.
  const isPreview = params.get("preview") === "1";
  const previewPremise = params.get("premise") ?? undefined;
  const previewRoom = params.get("room") ?? undefined;
  if (isPreview && previewPremise && previewRoom) {
    return (
      <Launcher
        premiseId={previewPremise}
        roomId={previewRoom}
        preview={true}
      />
    );
  }

  // Otherwise the only way in is a real pairing. If there's no local binding,
  // the gate first tries to re-attach by hardware serial (recovers a TV whose
  // storage was wiped on a hard reboot) before falling back to pairing.
  return <PairedGate />;
}

type BootPhase =
  | { phase: "paired"; binding: Binding }
  | { phase: "checking" }
  | { phase: "reconnecting" }
  | { phase: "unpaired" };

/**
 * Is there a native identification surface AT ALL - as distinct from whether
 * it can answer a hardware SERIAL right now. `FoyerNative.getDeviceInfo`
 * (Android) and `webapis.productinfo` (Tizen) are the CONTAINERS that make
 * identifiers available in the first place; both are present as soon as the
 * platform's privileged bridge has loaded, which is not the same moment a
 * specific call on them (`getDuid()`) is guaranteed to answer - see
 * `reattachBySerial()`'s own retry below for why that distinction matters.
 * On a plain web/browser session (no native shell) this is false and stays
 * false, which is exactly the "nothing to ever retry" case that keeps a
 * preview/browser tab from being sent through a pointless retry loop.
 */
function hasNativeIdentitySurface(): boolean {
  if (typeof window.FoyerNative?.getDeviceInfo === "function") return true;
  const pi = (window as unknown as { webapis?: { productinfo?: unknown } })
    .webapis?.productinfo;
  return typeof pi === "object" && pi !== null;
}

function initialBoot(): BootPhase {
  const b = getBinding();
  if (b) return { phase: "paired", binding: b };
  // No binding at boot. Distinguish "storage lost it entirely" from "storage
  // kept a record we rejected" (getBinding() drops a binding missing any of
  // premiseId/roomId/screenToken), and note whether the raw key is still there
  // at all - that separates a wiped store from a bad write.
  noteBoot(
    "no_binding",
    getSalvagedToken() ? "token_only" : hasRawBinding() ? "raw_bad" : "empty"
  );
  // Only attempt a server re-attach when there's a native identification
  // surface to retry a serial read against (native Philips/Tizen shell). On
  // web / a shell without one, go straight to pairing - no flash of a
  // loading screen. Gated on the SURFACE, not on whether `serial` happens to
  // already be readable THIS SYNCHRONOUS INSTANT - see
  // `hasNativeIdentitySurface()` above for why that used to matter.
  return hasNativeIdentitySurface()
    ? { phase: "checking" }
    : { phase: "unpaired" };
}

/**
 * How often to retry the serial-based re-attach while stuck on `unpaired`.
 * Comfortably above the server's own `REATTACH_QUIET_MS` (60s, see
 * `secondFactorFor` in `src/server/api/pairings.ts`) so a retry that lands
 * after the first failed one has a real chance of the "quiet long enough"
 * condition having become true meanwhile.
 */
export const REATTACH_RETRY_MS = 30_000;

/**
 * How many failed retries to spend on the neutral "Reconnecting…" screen
 * before falling back to the interactive pairing keypad. 6 attempts *
 * `REATTACH_RETRY_MS` = 3 minutes - comfortably longer than the ~60-150s
 * `known_network` recovery window CONFIRMED on real hardware 2026-09-09 (two
 * Philips power cycles, each one `known_network` history entry, screen back
 * online with no human action) - see [[tv-pairing-reattach]] in project
 * memory - but still bounded, so a device that genuinely never recovers
 * (wrong network, no server record, or the still-open Tizen serial-read race
 * documented in the same memory note) reaches an ACTIONABLE screen instead of
 * a silent splash with no way for staff to type a fresh code.
 */
export const RECONNECT_GRACE_ATTEMPTS = 6;

/**
 * Decides paired vs. pairing, with a serial-based re-attach in between. A TV
 * whose local storage was wiped (hotel clean-state on a hard reboot) has no
 * binding but still knows its serial; we ask the server which premise/room/token
 * it belongs to and restore the binding, skipping the pairing screen.
 *
 * The FIRST attempt fires immediately (shown as <BootScreen>, "checking"). If
 * that one loses the race - the network isn't fully up yet right after a
 * physical reboot, or the server's `known_network` factor needs the device to
 * have been quiet a little longer than it has been so far - we do NOT strand
 * the screen on <BootScreen> forever, NOR do we drop straight to the
 * interactive pairing keypad while a recovery this ordinary is still likely.
 *
 * 2026-09-07 (hasselbacken/302): a Tizen panel's ONE-SHOT resolve attempt
 * failed once, right after a reboot-triggered storage wipe, and nothing ever
 * asked again - the device sat on the pairing PIN screen indefinitely despite
 * the server being willing to let it straight back in moments later. Fixed by
 * retrying in the background every `REATTACH_RETRY_MS` instead of giving up.
 *
 * 2026-09-15: that fix showed the pairing keypad (six-digit code entry, "Enter
 * the code given from your installation partner") to the FIRST failed retry
 * onward - which for a device with a hardware serial almost always means an
 * ordinary, self-healing `known_network` recovery already in flight, not a
 * device that actually needs a human. A guest-facing Philips panel showed a
 * developer-shaped pairing screen for the ~1-2 minutes that recovery
 * genuinely takes, on every reboot. So a serial-bearing device now spends up
 * to `RECONNECT_GRACE_ATTEMPTS` failed retries on <BootScreen
 * label="Reconnecting…"> instead - and only falls through to the interactive
 * keypad once that grace period is spent, so a device that is NOT going to
 * self-heal still reaches an actionable screen rather than an indefinite
 * silent splash.
 *
 * A success at any point jumps straight to "paired" (no flash back through
 * "checking"/"reconnecting"). A device with no serial at all never enters
 * this loop (nothing to retry) and goes straight to the pairing keypad, as
 * before - there is no recovery to wait for, so no reason to delay it. A
 * device that genuinely needs a human (staff "Allow re-attach" window, or a
 * fresh pairing code) is unaffected once the grace period lapses - retrying
 * keeps costing one cheap POST per `REATTACH_RETRY_MS` in the background
 * while the keypad is shown, and changes nothing else on screen while it
 * keeps failing.
 */
function PairedGate() {
  const [boot, setBoot] = useState<BootPhase>(initialBoot);
  // Read inside the retry loop without making it a effect dependency - the
  // loop sets this same state itself (checking -> reconnecting -> unpaired)
  // and must not restart its own timer as a side effect of that transition.
  const bootRef = useRef(boot);
  bootRef.current = boot;

  useEffect(() => {
    // Already paired with a valid stored binding - nothing to resolve.
    // REGRESSION FIXED 2026-09-08: this guard used to be `boot.phase !==
    // "checking"` and got dropped when the retry loop was added (a49fb47,
    // 2026-09-07), so this effect started firing on EVERY mount, even an
    // already-paired one. `reattachBySerial()` always sends
    // `getSalvagedToken()` - which returns the CURRENT token off any stored
    // binding, valid or not - as `screenToken`. The server's
    // `secondFactorFor()` (src/server/api/pairings.ts) checks
    // `token_possession` first and accepts by definition whenever the
    // presented token matches the one on file, which it always does for an
    // already-paired device presenting its own token. That unconditionally
    // ROTATES the screen token (`configStore.rotateScreenToken`) on every
    // single app restart of every paired device with a hardware serial -
    // invalidating anything already in flight on the old token (heartbeat,
    // telemetry/model reporting, SSE) for no reason at all. This is the
    // fleet-wide "screens lose heartbeat / stop reporting model" instability
    // reported 2026-09-08. See [[tv-pairing-reattach]] part 3 in project
    // memory, which had this flagged as an open, unconfirmed bug.
    if (bootRef.current.phase === "paired") return;
    // No native identification surface - `initialBoot()` already sent us
    // straight to "unpaired" for this case, and there is nothing to ever
    // retry.
    //
    // REGRESSION FIXED 2026-09-15 (hasselbacken/302, found 2026-09-09): this
    // guard used to be `!bridge.getDeviceInfo()?.serial` - a SECOND
    // one-shot synchronous read of the exact same flaky field
    // `initialBoot()` already read once. `webapis.productinfo.getDuid()`
    // (Tizen) can still be settling for a beat after a genuine hardware
    // power cycle - longer, in practice, than the ~2.5s measured for an
    // ordinary widget relaunch (`hostReload.ts` - a full reboot
    // re-initializes the whole TV OS under the widget, not just the
    // widget). If BOTH one-shot reads lost that race, this bailed before
    // ever creating the retry timer - permanently: this effect is
    // mount-once, so nothing here ever ran again. Observed live: a Tizen
    // panel power-cycled, went straight to the pairing screen, and sat
    // there 13+ minutes with ZERO reattach attempts - not a slow retry, no
    // retry at all. See [[tv-pairing-reattach]] in project memory.
    //
    // Gating on `hasNativeIdentitySurface()` instead means this only skips
    // the loop when there is NOTHING to ever retry against (a plain web/
    // browser session) - a device with a real native shell always gets its
    // timer, and the actual serial VALUE is retried separately, inside
    // `reattachBySerial()`, on every attempt.
    if (!hasNativeIdentitySurface()) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let attempts = 0;

    const attempt = async () => {
      attempts += 1;
      const { binding: restored, serialUnknown } = await reattachBySerial();
      if (cancelled) return;
      if (restored) {
        setBoot({ phase: "paired", binding: restored });
        return; // Done - no further retries once actually paired.
      }
      // The server has NO record of this serial at all (2026-09-22 fix): a
      // brand-new/never-provisioned device, or one whose screen was deleted.
      // The grace period exists to ride out a RACE for a device the server
      // already knows - there is no race to ride out here, so waiting the
      // full RECONNECT_GRACE_ATTEMPTS just delays the actionable keypad for
      // nothing. Skip straight there. A device that IS known but not yet
      // satisfying a re-attach factor (403, the ordinary reboot-recovery
      // case) still gets the full grace period below, unchanged.
      if (serialUnknown) {
        if (bootRef.current.phase !== "unpaired") {
          noteBoot("reattach_unknown_serial", `${attempts}_attempts`);
          setBoot({ phase: "unpaired" });
        }
      } else if (bootRef.current.phase === "checking") {
        // First failure: leave <BootScreen> ("checking") for the neutral
        // "Reconnecting…" variant, not straight to the interactive keypad -
        // see the 2026-09-15 note above.
        setBoot({ phase: "reconnecting" });
      } else if (
        bootRef.current.phase === "reconnecting" &&
        attempts >= RECONNECT_GRACE_ATTEMPTS
      ) {
        // Grace period spent with no recovery - give staff/guest an
        // actionable screen instead of an indefinite silent splash.
        noteBoot("reattach_timeout", `${attempts}_attempts`);
        setBoot({ phase: "unpaired" });
      }
      // Once "unpaired", subsequent failures fall through here untouched -
      // still retrying every REATTACH_RETRY_MS in the background, but never
      // flipping the screen back to "reconnecting" (that would just flicker).
      timer = setTimeout(attempt, REATTACH_RETRY_MS);
    };

    void attempt();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // Deliberately mount-once: this owns its own retry cadence via refs/
    // timers. Depending on `boot.phase` here would re-fire immediately on
    // every checking->unpaired transition this same effect causes, instead of
    // waiting the intended `REATTACH_RETRY_MS`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (boot.phase === "checking") return <BootScreen />;
  if (boot.phase === "reconnecting")
    return <BootScreen label="Reconnecting…" />;
  if (boot.phase === "unpaired") return <PairingScreen />;
  return (
    <Launcher premiseId={boot.binding.premiseId} roomId={boot.binding.roomId} />
  );
}

/**
 * How many times to retry an empty serial read before concluding this
 * attempt genuinely has none, and how long to wait between attempts. 8 *
 * 500ms = 3.5s - comfortably past the ~2.5s settle time measured for an
 * ordinary Tizen widget relaunch (`hostReload.ts`), with margin for a real
 * hardware reboot (which re-initializes the whole TV OS under the widget,
 * not just the widget, so plausibly settles slower). Same shape as
 * `deviceModelWithRetry()` (`PairingScreen.tsx`) for the same underlying
 * reason: `webapis.productinfo`'s identifier calls only answer once the
 * privileged context has fully settled, and that can lag a beat behind the
 * CONTAINER itself existing (see `hasNativeIdentitySurface()` above).
 */
export const SERIAL_READ_RETRY_ATTEMPTS = 8;
export const SERIAL_READ_RETRY_DELAY_MS = 500;

/**
 * Ask the server to restore this TV's binding from its hardware serial. On a
 * match, persist the binding AND adopt the server's original screen id (so
 * heartbeats keep reporting under the same device record), then return it.
 * Returns null on no serial / no match / any error - caller shows pairing.
 *
 * The serial only NAMES the device; the server also requires a second factor
 * before it will re-bind (see POST /screens/resolve). We hand it the best one
 * we have: a screen token salvaged from a binding too damaged to use. When
 * there is none, the server falls back to recognising the premise's network -
 * or, failing that, answers 403 and the TV drops to the pairing screen, which
 * is the correct outcome for a device that can't prove who it is.
 *
 * The token the server returns is always FRESH (it rotates on every resolve),
 * so we must persist what came back, never what we sent.
 *
 * The serial read itself is retried a few times before giving up on THIS
 * attempt (not the same thing as giving up on ever recovering - the caller,
 * `PairedGate`, keeps calling this every `REATTACH_RETRY_MS` regardless).
 * 2026-09-15: closes the other half of the 2026-09-09 hasselbacken/302
 * finding - even once the retry TIMER reliably gets set up
 * (`hasNativeIdentitySurface()`, above), a single synchronous serial read
 * per attempt could still lose the settle-time race and quietly no-op every
 * single attempt for as long as that lasted, which would have looked
 * identical to "the server keeps rejecting it" from the outside. See
 * [[tv-pairing-reattach]] in project memory.
 */
interface ReattachResult {
  binding: Binding | null;
  /**
   * The server has NO record of this serial at all (`/screens/resolve`
   * answered 404 `not_found` - see `secondFactorFor` in
   * `src/server/api/pairings.ts`): a brand-new/never-provisioned device, or
   * one whose screen was deleted from the CMS. Distinct from 403
   * `second_factor_required`, where the serial IS known but the current
   * attempt doesn't satisfy a re-attach factor yet - that's the ordinary
   * reboot-recovery race `RECONNECT_GRACE_ATTEMPTS` exists to ride out.
   * `false` for every other failure (network error, other status, missing
   * serial) so the caller's default (wait out the grace period) still holds
   * whenever the server hasn't actually said "I have never heard of this."
   */
  serialUnknown: boolean;
}

async function reattachBySerial(): Promise<ReattachResult> {
  let serial = bridge.getDeviceInfo()?.serial;
  for (
    let attempt = 0;
    !serial && attempt < SERIAL_READ_RETRY_ATTEMPTS - 1;
    attempt += 1
  ) {
    await new Promise((resolve) =>
      setTimeout(resolve, SERIAL_READ_RETRY_DELAY_MS)
    );
    serial = bridge.getDeviceInfo()?.serial;
  }
  if (!serial) return { binding: null, serialUnknown: false };
  try {
    const salvaged = getSalvagedToken();
    const res = await fetch("/api/foyer/screens/resolve", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        serial,
        ...(salvaged ? { screenToken: salvaged } : {}),
      }),
    });
    if (res.status === 404) return { binding: null, serialUnknown: true };
    if (!res.ok) return { binding: null, serialUnknown: false };
    const body = (await res.json()) as {
      screenId?: string;
      premiseId?: string;
      roomId?: string;
      screenToken?: string;
    };
    if (!body.premiseId || !body.roomId || !body.screenToken) {
      return { binding: null, serialUnknown: false };
    }
    if (body.screenId) setScreenId(body.screenId);
    const binding: Binding = {
      premiseId: body.premiseId,
      roomId: body.roomId,
      screenToken: body.screenToken,
    };
    setBinding(binding);
    return { binding, serialUnknown: false };
  } catch {
    return { binding: null, serialUnknown: false };
  }
}

interface LauncherProps {
  premiseId: string;
  roomId: string;
  /** When true: TV-side render only (no heartbeat, no SSE, no pairing
   *  binding). Used by the CMS "Open preview" button. */
  preview?: boolean;
}

/** Build timestamp injected by Vite at build time. Falls back to a constant
 *  in dev so the CMS Devices view always shows something useful. */
const APP_VERSION =
  (import.meta.env?.VITE_APP_VERSION as string | undefined) ?? "dev";

/**
 * Which signage renderer a screen's `role: "signage"` boot uses - the one
 * decision `PremiseScreen.signageEngine` exists to drive (see that field's
 * own doc comment, `@shared/types/foyer.ts`). Extracted as its own named,
 * exported function so this real branch point is unit-testable directly:
 * `App.test.tsx` has no existing coverage that renders the signage role
 * branch at all (confirmed by direct search - it only covers a role of
 * "tv"), and a full App-level render for a one-field decision would be
 * disproportionate to what's actually being verified here.
 */
export function pickSignageComponent(
  signageEngine: PremiseScreen["signageEngine"]
): typeof SignageSurface {
  return signageEngine === "ported" ? SignageEngineSurface : SignageSurface;
}

function Launcher({ premiseId, roomId, preview }: LauncherProps) {
  const { config, loading, error, refresh, offline, savedAt, unreachable } =
    useConfig({ premiseId });

  // Promote this render to the last-good offline shell - but only once a real
  // config came back from the API. Committing while offline would pin the room
  // to whatever half-warm cache got it here; committing before a config exists
  // would bless a bundle that has not yet proved it can paint a launcher.
  const committedRef = useRef(false);
  useEffect(() => {
    if (preview || !config || offline || committedRef.current) return;
    committedRef.current = true;
    void commitGoodShell();
  }, [config, offline, preview]);

  // One breadcrumb per outage, so `log_capture` and the pairing screen's meta
  // strip can say a room ran on cached content - the heartbeat cannot report an
  // outage while it is happening, by definition.
  const wasOfflineRef = useRef(false);
  useEffect(() => {
    if (offline === wasOfflineRef.current) return;
    wasOfflineRef.current = offline;
    noteBoot(offline ? "offline" : "reconnected", error?.message);
  }, [offline, error]);

  // Latest CMS-configured streaming-app slug, read fresh by the heartbeat
  // without restarting its interval when the config refreshes.
  const streamingSlugRef = useRef<string | undefined>(undefined);
  streamingSlugRef.current = config?.tvStreamingAppSlug;

  // Latest heartbeat sender, so the realtime handler can trigger an immediate
  // ping on a `refresh_telemetry` command without restarting the ping interval.
  const pingRef = useRef<() => void>(() => {});

  // Panel state as CONFIRMED by the hardware (Phase 5 - energy). Only written
  // when `bridge.setPower()` reports the TV accepted the change, and reported
  // in the heartbeat so the server's energy log - and the ESG savings figure
  // built on it - reflects what the panel actually did. Stays undefined on a
  // device without real power control, so those devices contribute no saving
  // rather than a fictional one.
  const confirmedPowerRef = useRef<"on" | "standby" | undefined>(undefined);

  // Consecutive 401s from the heartbeat. Reset by any non-401 answer, so only
  // an unbroken run unpairs the TV - see the heartbeat handler below.
  const unauthorizedRef = useRef(0);

  // Open the realtime stream once we know which premise we're viewing. CMS
  // previews skip it (the server-side SSE endpoint requires a screen-token,
  // which a preview tab doesn't have).
  useEffect(() => {
    if (!premiseId || preview) return;
    const screenId = getScreenId();
    const binding = getBinding();
    if (!binding?.screenToken) return;
    // Ack a durable command, returning a promise so destructive handlers can
    // AWAIT the ack before reloading (a fire-and-forget ack races the reload
    // and loses, which is what caused the reboot loop). Bounded so a hung
    // network can never block the reload indefinitely.
    const ackWithResult = (
      commandId: string | undefined,
      result?: Record<string, unknown>
    ): Promise<void> => {
      const b = getBinding();
      if (!commandId || !b?.screenToken) return Promise.resolve();
      return Promise.race([
        ackCommand({ screenId, commandId, screenToken: b.screenToken, result }),
        new Promise<void>((r) => setTimeout(r, 2000)),
      ]);
    };
    const ack = (commandId: string | undefined): Promise<void> =>
      ackWithResult(commandId);

    // Single execution path for both live events and drained (offline-missed)
    // commands. Delivery is at-least-once, so we guard every commandId with a
    // PERSISTENT executed-set: a command seen again (e.g. re-drained after a
    // reboot reload) is acked-only and never re-run.
    const handleEvent = async (evt: RealtimeEvent) => {
      const cid = evt.commandId;
      if (cid && wasExecuted(cid)) {
        if (evt.type === "screenshot" || evt.type === "log_capture") {
          // A capture is marked executed when it STARTS, so "executed" does not
          // mean "delivered". Still running on this page: its upload is the
          // ack, so stay out of the way. Started by a page that has since died:
          // say so, with the stage it died in, instead of the blank ack that
          // made a capture that never happened read as a success.
          if (captureRunning(cid)) return;
          const stage = takeInterruptedStage(cid);
          if (stage) {
            await ackWithResult(cid, {
              error: `the page restarted during the capture (stage: ${stage})`,
              interruptedAt: stage,
            });
            return;
          }
        }
        // Already done on this device - just clear it server-side.
        await ack(cid);
        return;
      }

      if (evt.type === "config_changed" && evt.premiseId === premiseId) {
        refresh();
        if (cid) markExecuted(cid);
        await ack(cid);
      }
      if (
        evt.type === "reboot" &&
        evt.premiseId === premiseId &&
        evt.screenId === screenId
      ) {
        // Mark executed + await the ack BEFORE rebooting. Marking is
        // synchronous localStorage (survives the reload), and awaiting the ack
        // means the server clears the command - together these break the
        // reload→re-drain→reboot loop even if either alone fails.
        if (cid) markExecuted(cid);
        // Say which reboot actually happened. On a managed (device-owner) box
        // this is a real device reboot - the only thing that recovers a wedged
        // WebView; everywhere else it is the page restarting itself, which
        // cannot. The fleet must not be told those are the same event.
        const native = bridge.rebootDevice();
        await ackWithResult(cid, { method: native ? "device" : "reload" });
        if (native) {
          // The OS accepted it; the process dies when the box goes down. Keep a
          // fallback reload for the case where "accepted" didn't become
          // "rebooted" - a reload during a real reboot is harmless, a box that
          // silently did neither is a site visit.
          setTimeout(() => window.location.reload(), 10_000);
          return;
        }
        window.location.reload();
        return;
      }
      if (
        evt.type === "unpair" &&
        evt.premiseId === premiseId &&
        evt.screenId === screenId
      ) {
        // The screen was removed in the CMS - drop the local binding and
        // reload (preserving ?tv=1) so the app falls back to pairing.
        if (cid) markExecuted(cid);
        await ack(cid);
        noteBoot("unpair_event");
        clearBinding();
        window.location.reload();
        return;
      }
      if (
        evt.type === "refresh_telemetry" &&
        evt.premiseId === premiseId &&
        evt.screenId === screenId
      ) {
        // Send a heartbeat right now instead of waiting for the interval.
        pingRef.current();
        if (cid) markExecuted(cid);
        await ack(cid);
        return;
      }
      if (
        evt.type === "screenshot" &&
        evt.premiseId === premiseId &&
        evt.screenId === screenId &&
        cid
      ) {
        // Capture + upload acks the command server-side (upload IS the ack),
        // so we don't ack() here.
        const b = getBinding();
        if (b?.screenToken) {
          markExecuted(cid);
          void runScreenshot({
            screenId,
            commandId: cid,
            screenToken: b.screenToken,
          });
        }
        return;
      }
      if (
        evt.type === "log_capture" &&
        evt.premiseId === premiseId &&
        evt.screenId === screenId &&
        cid
      ) {
        const b = getBinding();
        if (b?.screenToken) {
          markExecuted(cid);
          void runLogCapture({
            screenId,
            commandId: cid,
            screenToken: b.screenToken,
          });
        }
        return;
      }
      if (
        evt.type === "ota_apply" &&
        evt.premiseId === premiseId &&
        evt.screenId === screenId
      ) {
        // Like reboot, the web_bundle path reloads/replaces the running app,
        // so it is marked executed and acked BEFORE applying - otherwise the
        // reload→re-drain loop fires. The apk/firmware paths hand off to a
        // native updater and can report its verdict first.
        if (cid) markExecuted(cid);
        if (evt.kind === "apk" && evt.url) {
          // Hand it to the native shell. The RETURN VALUE is the point: a
          // device with no updater - every Tizen panel, whose widget updates
          // itself from sssp_config.xml - silently did nothing, and acking that
          // as success told the OTA wizard it had adopted a build it never saw.
          await ackWithResult(cid, {
            applied: bridge.applyUpdate(evt.url),
            kind: "apk",
            platform: bridge.platform,
          });
        } else if (evt.kind === "firmware" && evt.url) {
          // Hand the OEM image to the TV's own updater (it reboots to flash).
          // Same rule: a device without a firmware trigger says so rather than
          // reporting a flash it cannot perform.
          await ackWithResult(cid, {
            applied: bridge.applyFirmwareUpdate({
              url: evt.url,
              version: evt.targetVersion,
              fileName: evt.fileName ?? "",
              sizeBytes: evt.sizeBytes ?? 0,
            }),
            kind: "firmware",
            platform: bridge.platform,
          });
        } else {
          // Acked BEFORE the reload, which replaces the running app - otherwise
          // the reload→re-drain loop fires.
          await ackWithResult(cid, { applied: true, kind: "web_bundle" });
          // web_bundle: hard-reload onto the freshly deployed bundle. The next
          // heartbeat reports the new appVersion, which is how adoption lands.
          window.location.reload();
        }
        return;
      }
      if (
        evt.type === "launch_app" &&
        evt.premiseId === premiseId &&
        evt.screenId === screenId
      ) {
        // Guest companion "open on my TV": launch the app through the bridge,
        // exactly as a tile click on the TV would.
        const launched = bridge.launchApp({
          name: evt.name,
          androidSlug: evt.androidSlug,
          tizenId: evt.tizenId,
          webosId: evt.webosId,
          iosScheme: evt.iosScheme,
          webUrl: evt.webUrl,
        });
        if (cid) markExecuted(cid);
        // False here means the platform had no id for this app, or no launch
        // mechanism at all - which on Samsung is the common case until the
        // catalogue's `tizenId` column is filled (TIZEN-14). Reporting it is
        // how that gap becomes visible from a desk instead of from the lobby.
        await ackWithResult(cid, {
          applied: launched,
          appName: evt.name ?? null,
          platform: bridge.platform,
        });
        return;
      }
      if (
        evt.type === "set_ntp" &&
        evt.premiseId === premiseId &&
        evt.screenId === screenId
      ) {
        // Re-point the device's NTP (time) server.
        //
        // The verdict matters more here than anywhere. A signage schedule is
        // resolved from wall-clock, so a panel with the wrong time plays the
        // wrong thing or nothing at all - and this is the one command that
        // fixes it. Acking a silent no-op as success meant an operator could
        // "correct the clock" on a Samsung panel, see it succeed, and still be
        // looking at a screen that says nothing is scheduled.
        const ntpApplied = bridge.setNtpServer(evt.ntpServer);
        if (cid) markExecuted(cid);
        await ackWithResult(cid, {
          applied: ntpApplied,
          ntpServer: evt.ntpServer,
          platform: bridge.platform,
        });
        return;
      }
      if (
        evt.type === "power" &&
        evt.premiseId === premiseId &&
        evt.screenId === screenId
      ) {
        // Scheduled display on/off - native fast standby, app keeps running.
        // No-op + already-acked on a device without power control. Record the
        // new state ONLY when the TV confirms it, and push it immediately so
        // the energy report doesn't wait up to 30s for the next heartbeat.
        const powered = bridge.setPower(evt.on);
        if (powered) {
          confirmedPowerRef.current = evt.on ? "on" : "standby";
          pingRef.current();
        }
        if (cid) markExecuted(cid);
        // Already the rule for the ESG figure - an unconfirmed switch is not
        // counted - so the command history should not claim more than the
        // energy report does.
        await ackWithResult(cid, {
          applied: powered,
          on: evt.on,
          platform: bridge.platform,
        });
        return;
      }
      if (
        evt.type === "checkin_signal" &&
        evt.premiseId === premiseId &&
        evt.screenId === screenId
      ) {
        // A guest checked in: tell the firmware the room is occupied so it
        // keeps their app logins for the stay. Nothing to reset and no reload -
        // this only changes what the TV does at its NEXT power-off. Acked with
        // the firmware's verdict so the fleet can see which sets honour it.
        const signal = await bridge.signalGuestCheckIn();
        if (cid) markExecuted(cid);
        await ackWithResult(cid, signal as unknown as Record<string, unknown>);
        return;
      }
      if (
        evt.type === "checkout_wipe" &&
        evt.premiseId === premiseId &&
        evt.screenId === screenId
      ) {
        // Guest turnover. `markExecuted` FIRST - it's synchronous localStorage
        // and survives the reload below, so it (not the ack) is what breaks the
        // wipe→reload→re-drain→wipe loop. That frees us to run the wipe before
        // acking, which matters: the ack carries the device's own report of what
        // it managed to clear, including whether the FIRMWARE confirmed it wiped
        // native app logins. The server audits that verbatim, so a property's
        // trail reflects hardware truth rather than our intent.
        if (cid) markExecuted(cid);
        const report = await runTvCheckoutWipe();
        await ackWithResult(cid, report as unknown as Record<string, unknown>);
        window.location.reload();
        return;
      }
      // show_message (and any future broadcast events) → overlay/listeners.
      eventBus.emit(evt);
      if (evt.type === "show_message") {
        if (cid) markExecuted(cid);
        await ack(cid);
      }
    };

    /**
     * Drain whatever the realtime channel did not deliver.
     *
     * Shared by the (re)connect hook below and the POLL that backs it up.
     */
    const drain = async () => {
      const b = getBinding();
      if (!b?.screenToken) return;
      const cmds = await fetchPendingCommands({
        screenId,
        screenToken: b.screenToken,
      });
      // Sequential, so two destructive actions never fire at once. The
      // persistent executed-set guarantees each command runs only once across
      // reloads, so re-draining is free.
      for (const cmd of cmds) {
        const evt = commandToEvent(cmd);
        if (evt) await handleEvent(evt);
      }
    };

    /**
     * THE FLOOR UNDER COMMAND *AND CONFIG* DELIVERY. Only the command half of
     * this was here until 2026-09-07.
     *
     * The drain used to run only from `onOpen` - i.e. only when the realtime
     * connection managed to open. A device that speaks HTTPS but cannot
     * establish the WebSocket therefore heartbeated perfectly, appeared green
     * in the fleet console, and received NOTHING: not a reboot, not a
     * screenshot, not a log capture, not an unpair. Every command sat at
     * "sent" forever and the console had no way to say why.
     *
     * Measured on a Samsung panel 2026-08-13, where it also took away the one
     * diagnostic channel that could have explained it.
     *
     * A poll is the wrong shape for a live channel and the right shape for a
     * guarantee: realtime stays the fast path, and this ensures a command is
     * never more than one interval from being seen, whatever the transport is
     * doing. It costs one cheap GET per device per interval - deliberately
     * slower than the heartbeat, because this is a backstop and not a bus.
     *
     * `config_changed` needed the exact same guarantee and didn't have it:
     * it's a ONE-SHOT `publish()` in `screens.ts`/`foyer.ts` with a
     * `.catch(() => {})` on failure - no Command-collection row, no retry
     * sweep, nothing to drain. Its only backstop was `useConfig`'s
     * `FALLBACK_POLL_MS` (5 minutes) - five times slower than this timer.
     * Confirmed live on hasselbacken/302 (2026-09-07): a `show_message`
     * command through this same drain landed in ~33s, while a role flip's
     * `config_changed` on the identical connection took over two minutes -
     * "the message got through but the role change didn't" was the actual
     * bug report. `refresh()` now rides this same interval, for the same
     * reason it already rides `onOpen` below - a missed `config_changed` is
     * exactly as recoverable as a missed command, so it gets the same floor.
     * See [[tv-realtime-sync]].
     */
    const drainTimer = window.setInterval(() => {
      refresh();
      void drain().catch(() => {
        /* offline; the next tick tries again */
      });
    }, COMMAND_DRAIN_MS);

    const close = connectRealtime({
      premiseId,
      roomId,
      screenId,
      screenToken: binding.screenToken,
      onEvent: (evt) => void handleEvent(evt),
      // On every (re)connect, drain any commands missed while offline and run
      // them through the same handler. Sequential await so we don't fire two
      // destructive actions at once; the persistent executed-set guarantees
      // each command runs only once across reloads.
      onOpen: () => {
        // A config_changed event can just as easily have been missed while
        // disconnected as a command can - re-fetch on every (re)connect for
        // the same reason we drain commands below, rather than waiting for
        // the next explicit event or the useConfig fallback timer.
        refresh();
        void drain().catch(() => {});
      },
      // Purely diagnostic - the transport (Web PubSub SDK / EventSource) owns
      // reconnection on its own. Logged so a dropped realtime connection shows
      // up in a captured logcat instead of only being visible as "the config
      // went stale for a while" after the fact.
      onError: () => {
        // eslint-disable-next-line no-console
        console.warn("[realtime] connection dropped, reconnecting...");
      },
    });
    // First drain immediately, so a command issued while this device was
    // reloading is not held for a whole interval.
    void drain().catch(() => {});
    return () => {
      window.clearInterval(drainTimer);
      close();
    };
  }, [premiseId, roomId, preview, refresh]);

  // Door sign vs in-room TV vs signage. The role lives on this device's own
  // screen record in the served config. Resolved here, ahead of the
  // heartbeat effect below, on purpose: that effect's synchronous first
  // `ping()` on mount reads the signage-report cache, and the reset effect
  // right below this needs to have already run by then - see its comment.
  const myScreen = config?.screens?.find((s) => s.id === getScreenId());
  const myRole = myScreen?.role ?? "tv";

  // The signage-report cache (`signagePlayback.ts`) is a module-level slot
  // that `SignageSurface` writes into and never clears on unmount by design
  // (see that module's comment) - a stale-but-honest last report was meant
  // to be more useful than a hole. In practice that meant a screen that
  // genuinely flips away from `signage` keeps forwarding its last cached
  // report on every heartbeat forever, because the payload is only ever
  // absent when this slot was never written - so `ScreenSnapshot.signage`
  // could say "playing" indefinitely after a role flip to `tv`/`door-sign`
  // had already landed correctly (found investigating the 2026-08-07
  // hasselbacken incident - see [[tv-realtime-sync]]). Clearing it here,
  // keyed on the resolved role rather than on SignageSurface's own
  // lifecycle, means the very next heartbeat after a role leaves `signage`
  // is the one that tells the server "nothing to report" again. Declared
  // (and therefore committed) BEFORE the heartbeat effect below, so its
  // synchronous first `ping()` on mount already sees a cleared cache rather
  // than racing it.
  useEffect(() => {
    if (myRole !== "signage") {
      resetSignagePlayback();
    }
  }, [myRole]);

  // Heartbeat ping every 30s once bound to a premise. Updates the server's
  // in-memory online status so the CMS Devices view reflects reality. The
  // payload includes telemetry (model, app version, resolution, uptime,
  // current page) so the device detail modal stays useful.
  //
  // Skipped in preview mode - the heartbeat endpoint requires a real
  // screen-token, which preview tabs don't have.
  useEffect(() => {
    if (!premiseId || preview) return;
    const screenId = getScreenId();
    const startedAt = Date.now();
    let aborted = false;
    function ping() {
      const binding = getBinding();
      if (!binding?.screenToken) return;
      const net = bridge.getNetworkInfo();
      const payload = {
        premiseId,
        roomId,
        model: deviceModel(),
        platform: devicePlatform(),
        firmware: bridge.getDeviceInfo()?.firmware,
        // The hardware serial, on EVERY ping rather than only at redeem.
        //
        // A device that paired before its platform could read a serial (every
        // Samsung panel, until the WGT declared `productinfo`) had `serial:
        // null` written once and no way to ever correct it. That is not just a
        // blank field in the CMS: `reattachBySerial()` is the only thing that
        // restores a TV whose storage was wiped, and it needs the server to
        // already know the serial. Without this the panel loses its room on
        // every clean-state reboot, forever.
        //
        // The server only fills a serial that is MISSING - see
        // configStore.backfillScreenSerial - so a ping can never move a serial
        // from one screen to another.
        serial: bridge.getDeviceInfo()?.serial,
        capabilities: deviceCapabilities(),
        // Only ever the hardware-confirmed panel state (undefined until this
        // device has actually switched one) - the server treats a
        // device-reported state as authoritative over the scheduler's intent.
        powerState: confirmedPowerRef.current,
        // Prefer the real installed APK version from the native shell; fall back
        // to the build-time constant on web (where it's "dev").
        appVersion: bridge.appVersion() ?? APP_VERSION,
        // The web-bundle version, ALWAYS (even on native, where appVersion is
        // the APK) - this is what web_bundle OTA adoption tracks, so a reload
        // onto a new bundle registers on Android too.
        webVersion: APP_VERSION,
        streamingAppVersion:
          bridge.streamingAppVersion(streamingSlugRef.current) ?? undefined,
        resolution: `${window.screen.width}x${window.screen.height}`,
        uptimeMs: Date.now() - startedAt,
        wifiIp: net?.wifiIp,
        ethIp: net?.ethIp,
        wifiSignal: net?.wifiSignal,
        userAgent:
          typeof navigator !== "undefined" ? navigator.userAgent : undefined,
        // Has this panel been showing something other than Foyer? Undefined
        // when it never has, so a healthy fleet carries nothing. Only
        // UNPROMPTED losses count - a guest opening Netflix is not a fault, and
        // folding those in would bury the signal (TIZEN-15).
        foreground: foregroundReport(),
        // On-panel media cache state (TIZEN-9): entries, size, queue depth,
        // and - the one that matters - whether caching was disabled and why.
        // A silent kill switch is how the reference player spent weeks writing
        // files that were never served.
        signageCache: signageCacheReport(),
        // The FCM wake token (VC-28), so the server can ring this box even when
        // the WebView is backgrounded behind a native app. `null` on web / a
        // build without Firebase - the server treats that as "clear my token".
        fcmToken: bridge.getFcmToken(),
        // What this wall is ACTUALLY playing, when it is a signage screen.
        // Null on a TV or a door sign, and on a signage screen that has not
        // rendered yet - the server treats absent as "nothing to say" rather
        // than as an idle report.
        signage: currentSignagePlayback() ?? undefined,
        // Absent until something has played. "This screen played nothing today"
        // and "this screen is not a playback surface" are different facts, and
        // a wall of zeroes across a hotel's door signs would bury the one room
        // that matters.
        playback: currentPlaybackTally() ?? undefined,
      };
      void fetch(`/api/foyer/screens/${screenId}/heartbeat`, {
        method: "POST",
        headers: {
          "content-type": "application/json",
          "X-Screen-Token": binding.screenToken,
        },
        body: JSON.stringify(payload),
      })
        .then(async (res) => {
          if (aborted) return;
          // 401 = the screen token no longer resolves, i.e. the screen was
          // removed in the CMS. Drop the binding and reload (preserving
          // ?tv=1) so the app returns to the pairing screen. This is the
          // reliable fallback if the realtime `unpair` event was missed.
          //
          // But NOT on a single 401. Unpairing is irreversible on a device
          // with no hardware serial (a plain STB can't re-attach - see
          // PairedGate), and a 401 is not proof the CMS removed us: at boot
          // the TV may be behind a captive portal or hotel proxy that answers
          // 401 before the WAN is up, an ingress/WAF can 401 during a cold
          // start, and the server itself can 401 transiently while a premise
          // write is in flight. Any of those would unpair a perfectly good TV
          // on its first ping after a reboot. A real removal keeps answering
          // 401 forever, so requiring consecutive failures costs us ~1 minute
          // of lag on a genuine unpair and saves us from every transient one.
          if (res.status === 401) {
            unauthorizedRef.current += 1;
            noteBoot(
              "heartbeat_401",
              `${res.status} #${unauthorizedRef.current}`
            );
            if (unauthorizedRef.current >= UNAUTHORIZED_STRIKES) {
              // 2026-09-25 (hasselbacken/302): try the STRONGEST proof this
              // device still has before throwing it away. `reattachBySerial()`
              // always presents `getSalvagedToken()` - the token we are
              // about to delete - as `screenToken`, and the server's
              // `token_possession` factor (pairings.ts's `secondFactorFor`)
              // is a direct match against the token on file. If these 401s
              // were caused by something transient on the SERVER side (a
              // deploy's container swap, a cold-starting instance - both
              // named explicitly in the comment above) rather than a genuine
              // revocation, the old token is very often STILL valid, and
              // this succeeds on the very first round trip - the screen
              // silently keeps playing whatever it already was, never drops
              // to "Reconnecting…", and never needs the network+quiet
              // fallback PairedGate's own retry loop depends on (which is
              // exactly the factor that turned out to be too strict for
              // ordinary DHCP churn on the SAME network - see project memory
              // for the incident this fixes). `reattachBySerial()` persists
              // a restored binding itself (`setBinding`), so nothing here
              // needs to do anything further - the very next heartbeat tick
              // reads the durable store fresh and already has the fixed
              // token.
              if (hasNativeIdentitySurface()) {
                const { binding: restored } = await reattachBySerial();
                if (restored) {
                  unauthorizedRef.current = 0;
                  return;
                }
              }
              // Genuine unpair, or a device with no serial to prove
              // possession with at all - only now give up on the stored
              // binding. PairedGate's own retry loop (reattachBySerial
              // again, then network+quiet, then the interactive keypad)
              // takes over unchanged from here.
              clearBinding();
              window.location.reload();
            }
            return;
          }
          // Any non-401 answer proves the token still authenticates (or that
          // we were talking to something that isn't our API) - reset the count
          // so only an UNBROKEN run of 401s ever unpairs.
          unauthorizedRef.current = 0;
        })
        .catch(() => {
          // network blip - next ping will recover
        });
    }
    pingRef.current = ping;
    ping();
    const t = window.setInterval(() => {
      if (!aborted) ping();
    }, 30_000);
    return () => {
      aborted = true;
      pingRef.current = () => {};
      window.clearInterval(t);
    };
  }, [premiseId, roomId, preview]);

  // Durable log shipping (2026-09-25 diagnostics) - same lifecycle guard as
  // the heartbeat above (a real screen-token, not a preview tab), started
  // once per bound session. See logShipper.ts's own doc comment for why this
  // exists: the in-app ring buffer alone cannot survive an unattended
  // multi-day soak.
  useEffect(() => {
    if (!premiseId || preview) return;
    const screenId = getScreenId();
    const stop = startLogShipper(screenId, () => getBinding()?.screenToken);
    return stop;
  }, [premiseId, preview]);

  if (loading && !config) {
    return <BootScreen label="Connecting…" />;
  }

  // Common-area signage. Same panel, same pairing, same heartbeat - it simply
  // plays a scheduled loop instead of a launcher. This is the branch that makes
  // "one screen, three products" literally true: role is a per-screen setting,
  // so the same hardware is an in-room TV, a corridor nameplate or a lobby
  // display depending on one field.
  if (config && !preview && myRole === "signage") {
    const token = getBinding()?.screenToken;
    if (token) {
      // `signageEngine` (@shared/types/foyer's `PremiseScreen`, that
      // field's own doc comment) is this port's primary rollback lever,
      // chosen deliberately over redeploying: the web bundle has no
      // canary, so any device reload adopts whatever is on `main`
      // unconditionally - code-path selection has to be runtime-data-
      // driven. Absent -> "legacy", the only renderer any real screen has
      // ever used; NOT wired for a real rollout yet (Phase 0 of the port's
      // own plan - both hasselbacken screens stable - is still unmet as of
      // this line landing), so this branch exists but nothing sets the flag.
      const Signage = pickSignageComponent(myScreen?.signageEngine);
      return (
        <>
          <Signage
            screenId={getScreenId()}
            source={{ kind: "screen", token }}
            logoPlacement={config.channelLogoPlacement}
          />
          <OfflineBadge
            offline={offline}
            savedAt={savedAt}
            locale={config.defaultLocale}
            surface="signage"
            fixed
          />
        </>
      );
    }
  }

  if (config && !preview && myRole === "door-sign") {
    // Portrait is a per-device layout toggle on the same renderer (spec §3);
    // absent => the landscape default we ship.
    const orientation = isDoorSignOrientation(myScreen?.doorSignOrientation)
      ? myScreen.doorSignOrientation
      : "landscape";
    return (
      <>
        <DoorSignSurface
          config={config}
          roomId={roomId}
          orientation={orientation}
        />
        <OfflineBadge
          offline={offline}
          savedAt={savedAt}
          locale={config.defaultLocale}
          surface="door-sign"
          fixed
        />
      </>
    );
  }

  // Every premise renders a bespoke scheme (migration guarantees a binding;
  // the legacy `pmsConfig.defaultScheme` is still honoured as a fallback). The
  // scheme owns the whole viewport - its own navigation, focus, and layout.
  const schemeKey = config?.scheme?.key ?? config?.pmsConfig?.defaultScheme;
  if (config && isSchemeKey(schemeKey)) {
    return (
      <div className="foyer-app-shell">
        <div className="foyer-app" data-testid="app">
          <SchemeRouter schemeKey={schemeKey} config={config} roomId={roomId} />
          <OfflineBadge
            offline={offline}
            savedAt={savedAt}
            locale={config.defaultLocale}
            surface="scheme"
          />
        </div>
        <MessageOverlay />
      </div>
    );
  }

  // Nothing to render and nothing cached, because we never reached an API at
  // all - a brand-new box whose very first boot has no WAN. The hook keeps
  // retrying underneath us, so this resolves itself; say so rather than blaming
  // the premise for a network outage. An API that DID answer (404, 500) falls
  // through to the configuration error below, which is a different problem with
  // a different owner.
  if (!config && error && unreachable) {
    return (
      <div className="foyer-error" data-testid="offline-cold">
        <div>
          <div className="foyer-error__logo">Foyer</div>
          <div className="foyer-error__label">
            Waiting for the network — this screen has no saved content yet.
          </div>
          <div
            className="foyer-error__label"
            style={{ marginTop: 8, opacity: 0.5 }}
          >
            {premiseId} · {error.message}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="foyer-error">
      <div>
        <div className="foyer-error__logo">Foyer</div>
        <div className="foyer-error__label">
          No configuration for premise{" "}
          <strong data-testid="error-premise">{premiseId}</strong>.
        </div>
        {error && (
          <div
            className="foyer-error__label"
            style={{ marginTop: 8, opacity: 0.5 }}
          >
            {error.message}
          </div>
        )}
      </div>
    </div>
  );
}
